import { CircleAlert, CircleCheckBig, Info, TriangleAlert, X } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';
import { useLatest } from '../lib/hooks';

export type ToastStatus = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export interface ToastAction {
  label: string;
  onClick: () => void;
  /** Append the remaining seconds to the label ("Undo · 9s"). */
  countdown?: boolean;
}

export interface ToastOptions {
  /** Reusing an id replaces the existing toast. */
  id?: string;
  /** Bold lead-in ("Screenshot upload failed."). */
  title?: ReactNode;
  message?: ReactNode;
  /** Colour of the status dot/icon; `error` uses the assertive alert style. Default `success`. */
  status?: ToastStatus;
  /** Status dot (default) or icon. */
  indicator?: 'dot' | 'icon';
  /** Outline action, e.g. { label: 'Undo', countdown: true }. Closes the toast when clicked. */
  action?: ToastAction;
  /** Filled primary action, e.g. "Next step ▸". Closes the toast when clicked. */
  secondaryAction?: ToastAction;
  /**
   * Auto-dismiss delay in ms. Defaults: 3000; 10000 when `action.countdown` is set; errors stay
   * until dismissed. `null` = until dismissed. Timers pause while hovered or focused.
   */
  duration?: number | null;
  /** Show a × button (default: only for errors). */
  dismissible?: boolean;
  onDismiss?: () => void;
}

export interface ToastRecord extends Omit<ToastOptions, 'id' | 'duration'> {
  id: string;
  duration: number | null;
  status: ToastStatus;
}

export interface ToastApi {
  /** Show a toast; returns its id. A plain node is treated as `{ message }`. */
  toast: (options: ToastOptions | ReactNode) => string;
  success: (message: ReactNode, options?: Omit<ToastOptions, 'message' | 'status'>) => string;
  error: (message: ReactNode, options?: Omit<ToastOptions, 'message' | 'status'>) => string;
  warning: (message: ReactNode, options?: Omit<ToastOptions, 'message' | 'status'>) => string;
  info: (message: ReactNode, options?: Omit<ToastOptions, 'message' | 'status'>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export interface ToastProviderProps {
  children?: ReactNode;
  /** Distance from the bottom edge in px (Admin 24, Workspace 56 above the bottom bar). */
  offset?: number;
  /** Default auto-dismiss for non-error toasts (ms). */
  duration?: number;
  /** Maximum visible toasts (oldest are dropped). */
  max?: number;
  labels?: Partial<{ dismiss: string; region: string }>;
}

let counter = 0;

function isOptions(value: ToastOptions | ReactNode): value is ToastOptions {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !('$$typeof' in (value as object)) &&
    ('message' in (value as object) || 'title' in (value as object))
  );
}

/**
 * Bottom-centred toasts. Non-error toasts render in an `aria-live="polite"` status region,
 * errors in an assertive alert region. F8 moves focus to the newest toast.
 */
export function ToastProvider({
  children,
  offset = 24,
  duration = 3000,
  max = 3,
  labels,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const toastsRef = useLatest(toasts);
  const regionRef = useRef<HTMLDivElement>(null);
  const l = { dismiss: 'Dismiss', region: 'Notifications', ...labels };

  const dismiss = useCallback(
    (id: string) => {
      const found = toastsRef.current.find((t) => t.id === id);
      found?.onDismiss?.();
      setToasts((list) => list.filter((t) => t.id !== id));
    },
    [toastsRef],
  );

  const push = useCallback(
    (input: ToastOptions | ReactNode, forcedStatus?: ToastStatus) => {
      const options: ToastOptions = isOptions(input) ? input : { message: input };
      const status = forcedStatus ?? options.status ?? 'success';
      counter += 1;
      const id = options.id ?? `toast-${counter}`;
      const resolvedDuration =
        options.duration !== undefined
          ? options.duration
          : status === 'error'
            ? null
            : options.action?.countdown
              ? 10000
              : duration;
      const record: ToastRecord = { ...options, id, status, duration: resolvedDuration };
      setToasts((list) => [...list.filter((t) => t.id !== id), record].slice(-max));
      return id;
    },
    [duration, max],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast: (options) => push(options),
      success: (message, options) => push({ ...options, message }, 'success'),
      error: (message, options) => push({ ...options, message }, 'error'),
      warning: (message, options) => push({ ...options, message }, 'warning'),
      info: (message, options) => push({ ...options, message }, 'info'),
      dismiss,
      dismissAll: () => setToasts([]),
    }),
    [push, dismiss],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'F8') return;
      const target = regionRef.current?.querySelector<HTMLElement>('[data-toast] button');
      if (target) {
        event.preventDefault();
        target.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const errors = toasts.filter((t) => t.status === 'error');
  const others = toasts.filter((t) => t.status !== 'error');

  const region = (
    <div
      ref={regionRef}
      role="region"
      aria-label={l.region}
      className="pointer-events-none fixed inset-x-0 z-[80] flex flex-col items-center gap-2 px-4"
      style={{ bottom: offset }}
    >
      <div role="alert" aria-live="assertive" className="flex flex-col items-center gap-2">
        {errors.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={dismiss} dismissLabel={l.dismiss} />
        ))}
      </div>
      <div role="status" aria-live="polite" className="flex flex-col items-center gap-2">
        {others.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={dismiss} dismissLabel={l.dismiss} />
        ))}
      </div>
    </div>
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document === 'undefined' ? null : createPortal(region, document.body)}
    </ToastContext.Provider>
  );
}

/** Access the toast API. Must be used inside <ToastProvider>. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast() must be used inside <ToastProvider>.');
  return ctx;
}

/** @internal — lets the Gallery detect an existing provider. */
export const ToastContextInternal = ToastContext;

const DOT: Record<ToastStatus, string> = {
  success: 'bg-success',
  error: 'bg-error',
  warning: 'bg-warning',
  info: 'bg-info',
  neutral: 'bg-text-3',
};

function StatusIcon({ status }: { status: ToastStatus }) {
  const common = { 'aria-hidden': true, strokeWidth: 2.2 } as const;
  switch (status) {
    case 'success':
      return <CircleCheckBig {...common} className="size-4 shrink-0 text-success" />;
    case 'warning':
      return <TriangleAlert {...common} className="size-4 shrink-0 text-warning" />;
    case 'info':
      return <Info {...common} className="size-4 shrink-0 text-info" />;
    case 'error':
      return <CircleAlert {...common} className="size-4 shrink-0 text-error" />;
    default:
      return <span aria-hidden className={cn('size-2 shrink-0 rounded-full', DOT[status])} />;
  }
}

function ToastItem({
  toast,
  onClose,
  dismissLabel,
}: {
  toast: ToastRecord;
  onClose: (id: string) => void;
  dismissLabel: string;
}) {
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(toast.duration ?? 0);
  const [secondsLeft, setSecondsLeft] = useState(() => Math.ceil((toast.duration ?? 0) / 1000));
  const onCloseRef = useLatest(onClose);
  const countdown = Boolean(toast.action?.countdown) && toast.duration !== null;

  useEffect(() => {
    if (toast.duration === null || paused) return;
    const startedAt = Date.now();
    const deadline = startedAt + remainingRef.current;
    const timeout = window.setTimeout(() => onCloseRef.current(toast.id), remainingRef.current);
    const interval = countdown
      ? window.setInterval(() => setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000))), 250)
      : undefined;
    return () => {
      window.clearTimeout(timeout);
      if (interval !== undefined) window.clearInterval(interval);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [paused, toast.duration, toast.id, countdown, onCloseRef]);

  const isError = toast.status === 'error';
  const dismissible = toast.dismissible ?? isError;
  const runAction = (action: ToastAction) => {
    action.onClick();
    onClose(toast.id);
  };

  const pauseHandlers = {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    onFocus: () => setPaused(true),
    onBlur: () => setPaused(false),
  };

  if (isError) {
    return (
      <div
        data-toast=""
        {...pauseHandlers}
        className="pointer-events-auto flex w-[min(480px,calc(100vw-32px))] items-start gap-2.5 rounded-md border border-s-[3px] border-border border-s-error bg-surface px-3 py-2.5 text-13 text-text shadow-md animate-toast-in"
      >
        <span className="mt-0.5 inline-flex">
          <StatusIcon status="error" />
        </span>
        <span className="min-w-0 flex-1">
          {toast.title ? <b>{toast.title}</b> : null}
          {toast.title && toast.message ? ' ' : null}
          {toast.message}
          {toast.action ? (
            <>
              {' '}
              <button type="button" className="link font-medium" onClick={() => runAction(toast.action!)}>
                {toast.action.label}
              </button>
            </>
          ) : null}
          {toast.secondaryAction ? (
            <>
              {' · '}
              <button
                type="button"
                className="link font-medium"
                onClick={() => runAction(toast.secondaryAction!)}
              >
                {toast.secondaryAction.label}
              </button>
            </>
          ) : null}
        </span>
        {dismissible ? (
          <button
            type="button"
            aria-label={dismissLabel}
            onClick={() => onClose(toast.id)}
            className="grid size-5 shrink-0 place-items-center rounded-sm text-text-3 hover:text-text"
          >
            <X aria-hidden className="size-3.5" strokeWidth={2} />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-toast=""
      {...pauseHandlers}
      className="pointer-events-auto flex max-w-[min(640px,calc(100vw-32px))] items-center gap-2.5 rounded-md bg-inverse px-3.5 py-2.5 text-13 text-inverse-text shadow-lg animate-toast-in"
    >
      {toast.indicator === 'icon' ? (
        <StatusIcon status={toast.status} />
      ) : (
        <span aria-hidden className={cn('size-2 shrink-0 rounded-full', DOT[toast.status])} />
      )}
      <span className="min-w-0 flex-1">
        {toast.title ? <b>{toast.title}</b> : null}
        {toast.title && toast.message ? ' ' : null}
        {toast.message}
      </span>
      {toast.action ? (
        <button
          type="button"
          onClick={() => runAction(toast.action!)}
          className="h-[26px] shrink-0 whitespace-nowrap rounded-[6px] border border-[color-mix(in_srgb,var(--inverse-text)_28%,transparent)] bg-transparent px-2.5 text-12 font-medium text-inherit hover:bg-[color-mix(in_srgb,var(--inverse-text)_10%,transparent)]"
        >
          {toast.action.label}
          {countdown ? ` · ${secondsLeft}s` : null}
        </button>
      ) : null}
      {toast.secondaryAction ? (
        <button
          type="button"
          onClick={() => runAction(toast.secondaryAction!)}
          className="h-[26px] shrink-0 whitespace-nowrap rounded-[6px] bg-primary px-2.5 text-12 font-medium text-white hover:bg-primary-text"
        >
          {toast.secondaryAction.label}
        </button>
      ) : null}
      {dismissible ? (
        <button
          type="button"
          aria-label={dismissLabel}
          onClick={() => onClose(toast.id)}
          className="grid size-5 shrink-0 place-items-center rounded-sm opacity-60 hover:opacity-100"
        >
          <X aria-hidden className="size-3.5" strokeWidth={2} />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Static toast visual (no timers, not announced) — for documentation and empty-state previews.
 */
export interface ToastPreviewProps extends Omit<ToastOptions, 'duration' | 'id'> {
  /** Seconds shown next to a countdown action. */
  secondsLeft?: number;
  className?: string;
}

export const ToastPreview = forwardRef<HTMLDivElement, ToastPreviewProps>(function ToastPreview(
  { secondsLeft, className, ...rest },
  ref,
) {
  const status = rest.status ?? 'success';
  const record: ToastRecord = { ...rest, id: 'preview', status, duration: null };
  return <StaticToast ref={ref} toast={record} secondsLeft={secondsLeft} className={className} />;
});

const StaticToast = forwardRef<
  HTMLDivElement,
  { toast: ToastRecord; secondsLeft?: number | undefined; className?: string | undefined }
>(function StaticToast({ toast, secondsLeft, className }, ref) {
  if (toast.status === 'error') {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-start gap-2.5 rounded-md border border-s-[3px] border-border border-s-error bg-surface px-3 py-2.5 text-13 text-text shadow-md',
          className,
        )}
      >
        <span className="mt-0.5 inline-flex">
          <StatusIcon status="error" />
        </span>
        <span className="min-w-0 flex-1">
          {toast.title ? <b>{toast.title}</b> : null}
          {toast.title && toast.message ? ' ' : null}
          {toast.message}
          {toast.action ? (
            <>
              {' '}
              <button type="button" className="link font-medium" onClick={toast.action.onClick}>
                {toast.action.label}
              </button>
            </>
          ) : null}
        </span>
        {toast.dismissible !== false ? (
          <span aria-hidden className="grid size-5 shrink-0 place-items-center text-text-3">
            <X className="size-3.5" strokeWidth={2} />
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center gap-2.5 rounded-md bg-inverse px-3 py-2.5 text-13 text-inverse-text shadow-md',
        className,
      )}
    >
      {toast.indicator === 'icon' ? (
        <StatusIcon status={toast.status} />
      ) : (
        <span aria-hidden className={cn('size-2 shrink-0 rounded-full', DOT[toast.status])} />
      )}
      <span className="min-w-0 flex-1">
        {toast.title ? <b>{toast.title}</b> : null}
        {toast.title && toast.message ? ' ' : null}
        {toast.message}
      </span>
      {toast.action ? (
        <button
          type="button"
          onClick={toast.action.onClick}
          className="h-[26px] shrink-0 whitespace-nowrap rounded-[6px] border border-[color-mix(in_srgb,var(--inverse-text)_28%,transparent)] bg-transparent px-2.5 text-12 font-medium text-inherit"
        >
          {toast.action.label}
          {toast.action.countdown && secondsLeft !== undefined ? ` · ${secondsLeft}s` : null}
        </button>
      ) : null}
      {toast.secondaryAction ? (
        <button
          type="button"
          onClick={toast.secondaryAction.onClick}
          className="h-[26px] shrink-0 whitespace-nowrap rounded-[6px] bg-primary px-2.5 text-12 font-medium text-white"
        >
          {toast.secondaryAction.label}
        </button>
      ) : null}
    </div>
  );
});
